import * as React from 'react';

import { cn } from './lib/cn';

export type StatusBadgeTone = 'success' | 'warning' | 'danger' | 'info' | 'neutral';
export type StatusBadgeSize = 'sm' | 'md';
export type StatusBadgeShape = 'pill' | 'square';

/**
 * Background and foreground only — no border, no ring.
 *
 * The app's `Badge` carried a `ring-1 ring-inset`; the locked Batch-3 sheet has
 * no border at all, and promoting the ring would have blessed the divergence as
 * the standard.
 *
 * The sheet's warning pair is `--color-warning-100` over the amber
 * `--status-reserviert-fg` holds — a value with no `warning-*` token behind it. Rather than write a raw colour into the kit, the
 * warning tone uses the `warning-50 / warning-700` family the rest of the
 * system already uses — the one documented deviation from the sheet.
 */
const TONES: Record<StatusBadgeTone, { wrap: string; dot: string }> = {
  success: { wrap: 'bg-success-50 text-success-700', dot: 'bg-success-500' },
  warning: { wrap: 'bg-warning-50 text-warning-700', dot: 'bg-warning-500' },
  danger: { wrap: 'bg-danger-50 text-danger-700', dot: 'bg-danger-500' },
  info: { wrap: 'bg-info-50 text-info-700', dot: 'bg-info-500' },
  neutral: { wrap: 'bg-neutral-100 text-text-secondary', dot: 'bg-neutral-400' },
};

const SIZES: Record<StatusBadgeSize, string> = {
  sm: 'px-2 py-0.5 text-sm',
  md: 'px-2.5 py-0.5 text-base-sm',
};

export interface StatusBadgeProps {
  label: string;
  tone: StatusBadgeTone;
  size?: StatusBadgeSize;
  shape?: StatusBadgeShape;
  dot?: boolean;
  /** Replaces the dot: a state the tone alone cannot carry (a spinner, a check). Decorative — the label announces it. */
  icon?: React.ReactNode;
  /**
   * Opt in to yielding (IMM-656). A badge whose label is a catalogue value or
   * customer input is CONTENT, not chrome: inside a bounded container it has to
   * be able to give way, or its text is painted over whatever sits beside it.
   * Pass it at every such call site and nowhere else — the default below is a
   * deliberate guarantee written for the topbar, and inheriting it should be a
   * decision rather than an accident.
   *
   * `truncate` sits on an INNER span rather than on the badge: the badge is
   * `inline-flex`, and `text-overflow` has no effect on a flex container's
   * anonymous text child. The floor that keeps the ellipsis itself renderable
   * rather than clipped mid-glyph (BL-043) belongs to the CONTAINER, not here.
   */
  flexible?: boolean;
  /**
   * Rendered after the label, inside the pill — e.g. the chevron of a badge that
   * opens a menu. Presentational only: whatever makes the badge interactive is
   * the element that wraps it, which owns the tab stop, the key handling and the
   * focus ring (BL-166).
   */
  trailing?: React.ReactNode;
  className?: string;
}

export function StatusBadge({
  label,
  tone,
  size = 'md',
  shape = 'pill',
  dot = false,
  icon,
  flexible = false,
  trailing,
  className,
}: StatusBadgeProps) {
  const tones = TONES[tone];

  return (
    // `shrink-0 whitespace-nowrap` by default: a status badge is short, fixed
    // chrome. Several share the topbar's leading slot with the breadcrumb, and
    // without a ceiling flexbox charges each one part of any shortfall — which
    // wrapped „In Akquise" onto two lines and took width the breadcrumb needed
    // (IMM-610). The breadcrumb is the only way back to the Objekte list.
    <span
      title={flexible ? label : undefined}
      className={cn(
        'inline-flex items-center gap-1.5',
        shape === 'pill' ? 'rounded-full' : 'rounded-sm',
        SIZES[size],
        tones.wrap,
        flexible ? 'min-w-0 max-w-full' : 'shrink-0 whitespace-nowrap',
        className,
      )}
    >
      {icon !== undefined && (
        <span className="inline-flex h-3.5 w-3.5 shrink-0 items-center justify-center" aria-hidden>
          {icon}
        </span>
      )}
      {icon === undefined && dot && <span className={cn('h-1.5 w-1.5 shrink-0 rounded-full', tones.dot)} aria-hidden />}
      {flexible ? <span className="truncate">{label}</span> : label}
      {trailing}
    </span>
  );
}
